/**
 * Autofill the cart and submit the final order on staff intervention
 * (HITL-1551), and — HITL-1562 — on the post-car_exit auto timer and the kill
 * switch, each behind its own flag. The Finish Order CLICK deliberately has no
 * autofill: the button is disabled whenever the cart holds an invalid item
 * (isValidCartSelector runs the same predicate getValidCartItems drops by), so
 * an agent can never click Finish on a cart this feature would rescue.
 *
 * WHY THIS IS THIN. It fills the CART, then calls the ordinary `sendOrder`.
 * There is no parallel submit path: the earlier draft of this ticket forked one
 * to dodge `sendOrder`'s `getValidCartItems` filter, and paid for it by having to
 * re-implement order-metadata assembly and by silently dropping
 * `dispatchOrderSideEffects` (coupon/loyalty registration and the COUPON_ADDED
 * metric), `populateOrderRequests` (so the returning POS `check` had no request
 * to attach to) and `sendOrderMetrics`. Filling the cart instead makes the items
 * valid, so the filter keeps them and all of that comes along for free.
 *
 * ON `sendOrder`'s `isAIAutoMode` GUARD. This path opts out of it explicitly,
 * with `bypassAIAutoMode`. The ticket needs every escalated car covered, "AI up
 * or down", and by the time staff take a car the AI is already suppressed on its
 * own side (`escalation_sent`), so there is no second order to race.
 *
 * An earlier version of this file claimed no bypass was needed, because a staff
 * intervention dispatches `sendAgentInterception` before submitting and that
 * clears the flag. The flag does usually end up false — but not for that reason,
 * and not reliably: `ai.slice.ts` clears it on EVERY interception type, and
 * `TTS_SENT` is one of them, emitted by ordinary AI-driven ordering with no agent
 * involvement (menuSlice.ts, useTTSMessage, usePOSCart, useReadback...). Relying
 * on it meant the submit worked by accident and would silently skip any car that
 * happened not to emit one.
 *
 * THE CALLER WAITS FOR THIS. `cancelOrder` defers `sendEndSession` and
 * `setFinalOrderSubmitted(true)` — which resets the cart and messages — until
 * this settles, or until a cap expires. That is what lets the outcome metric
 * below be both accurate (computed after the send) and deliverable (emitted
 * before the session ends); firing and forgetting gave you one or the other.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { cartActions } from './cartSlice';
import { orderActions, sendOrder } from './orderSlice';
import { buildCartAutofillSelections } from '../utils/cartAutofill';
import { convertToMapUsingName } from '../utils';
import logger from '../utils/logger';
import { messagingActions } from './messagingSlice';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';

/** Stable New Relic token — count on this, not on the prose. */
export const AUTOFILL_FINAL_ORDER_LOG_TOKEN = 'AUTOFILL_FINAL_ORDER';
export const AUTOFILL_FINAL_ORDER_METRIC = 'AUTOFILL_FINAL_ORDER_METRIC';
/** 3.0 (HITL-1562): adds `source`. 3.1 (HITL-1607): adds
 * `bypass_ai_auto_mode` — whether this attempt was authorised past sendOrder's
 * AI-auto-mode guard, so a `not_submitted` blocked BY that guard (bypass
 * false: flag off, or a quantity hold) can be told apart in New Relic from one
 * the bypass should have rescued (bypass true — the send itself failed). */
export const AUTOFILL_FINAL_ORDER_METRIC_SCHEMA_VERSION = '3.1';

/**
 * Which trigger asked for the fill (HITL-1562). `source` on the metric is what
 * lets New Relic split outcomes per trigger: a `not_submitted` from a
 * post-car_exit timer is expected and common, the same outcome from a staff
 * intervention is rare and alarming — pooled, the benign case buries the bad
 * one. It also picks the governing kill switch (see the thunk).
 */
export enum AutofillFinalOrderSource {
  staffIntervention = 'staff_intervention',
  postCarExitTimer = 'post_car_exit_timer',
  killSwitch = 'kill_switch',
}

/**
 * What the attempt did. There is no agent-facing surface on this path — the
 * session is torn down moments later — so this metric IS the surface, both in
 * New Relic (`module = 'HITL'`) and on the event stream.
 */
export enum AutofillFinalOrderOutcome {
  /** Slots were filled, and the order was handed to `sendOrder`. */
  filledAndSubmitted = 'filled_and_submitted',
  /** Nothing needed filling; the order was submitted as it stood. */
  submittedUnchanged = 'submitted_unchanged',
  /** At least one required slot had no fillable option. Submitted anyway —
   * `sendOrder` drops only the still-invalid items, so the rest of the cart
   * still reaches staff, which beats sending nothing. */
  submittedIncomplete = 'submitted_incomplete',
  /** `sendOrder` shipped nothing: every item still invalid, an empty cart, or a
   * rejected request. The order did NOT reach the POS. */
  notSubmitted = 'not_submitted',
  /** The kill switch is off. */
  disabled = 'disabled',
}

export interface IAutofillFinalOrderResult {
  outcome: AutofillFinalOrderOutcome;
  /** One `modGroup=item` entry per fill — the "fills are logged" criterion. */
  fills: string[];
  /** Required groups the ladder could not fill. */
  unfillable: string[];
}

export interface IAutofillFinalOrderArgs {
  /** The same handlers interim orders use, so a failed FINAL submit surfaces
   * through the menu-module error UI. Threaded from the call site rather than
   * rebuilt here. */
  menuUnificationOrderFailureCallback?: Function;
  menuUnificationOrderSuccessCallback?: Function;
  /** Which trigger asked (HITL-1562). Defaults to the original
   * staff-intervention behaviour, so the HITL-1551 call sites are untouched. */
  source?: AutofillFinalOrderSource;
  /** `sendOrder`'s AI-auto-mode opt-out. `true` (the default) is right for a
   * staff intervention — see the header. The HITL-1562 paths pass the value of
   * `selectAutoSubmitBypassesAIAutoMode` (HITL-1607): the kill switch, minus
   * the quantity-hold exclusion — see that gate for why the bypass is safe. */
  bypassAIAutoMode?: boolean;
}

/** What `computeAutofillPlan` reads. Everything comes off the store slices the
 * thunk already reads; the gate below feeds it the same fields, so the two can
 * never disagree about whether a fill is needed. */
export interface IAutofillPlanInputs {
  cartItems: any;
  autoCombinedCartItems: any;
  brandMenu: any;
  modSymbolMapping: any;
  menuRes: any;
  suppressUpsellEnabled?: boolean;
  autoComboEnabled?: boolean;
  bmAutoComboEnabled?: boolean;
  /** Passed through to the fill ladder — the gate's dry run must not log. */
  quiet?: boolean;
}

/**
 * The ONE fill computation, shared by the thunk and by `selectNeedsAutofill`
 * (HITL-1562). A private copy in either place is how the gate and the fill
 * drift apart — a "gate says no, thunk would have filled" disagreement
 * silently loses the feature.
 *
 * ON `legacyAutoCombo`: HITL's client-side auto-combo is legacy — combining
 * moved to the backend, so a combined item reaches HITL as an ordinary cart
 * item and `autoCombinedCartItems` stays empty. The fill therefore works on
 * `cart.cartItems`, which is both what `sendOrder` ships and the only map
 * `selectModifiers` can write. If `autoCombinedCartItems` ever IS populated
 * (flags included — this mirrors `sendOrder`'s own ship condition EXACTLY),
 * DO NOT FILL: filling `cart.cartItems` anyway would leave `fills` non-empty
 * while `sendOrder` ships the combined cart, so the outcome would report
 * `filled_and_submitted` for a fill that never reached the POS — the one
 * failure this path must not produce. Submitting unfilled is honest: the order
 * still goes out, and the metric says nothing was filled (see HITL-1556).
 * With the plan computed off an empty map, `selections` comes back empty, so
 * the HITL-1562 gate reads false and those paths stay on today's code.
 */
export const computeAutofillPlan = ({
  cartItems,
  autoCombinedCartItems,
  brandMenu,
  modSymbolMapping,
  menuRes,
  suppressUpsellEnabled,
  autoComboEnabled,
  bmAutoComboEnabled,
  quiet,
}: IAutofillPlanInputs) => {
  const legacyAutoCombo =
    (autoComboEnabled ||
      (getMenuUnificationFeatureFlag() && bmAutoComboEnabled)) &&
    Object.values(autoCombinedCartItems ?? {}).length > 0;

  const plan = buildCartAutofillSelections(legacyAutoCombo ? {} : cartItems, {
    brandMenuConstraints: convertToMapUsingName(brandMenu?.constraints ?? []),
    modSymbolMapping: modSymbolMapping ?? {},
    posSettings: menuRes?.posSettings ?? {},
    suppressUpsellEnabled,
    quiet,
  });

  return { legacyAutoCombo, ...plan };
};

/**
 * "Would filling change anything?" — the HITL-1562 gate.
 *
 * Deliberately a plain function over state, NOT a memoized reactive selector:
 * it is only needed at the moment a trigger fires (`store.getState()`), and a
 * subscription would re-run the fill ladder on every cart edit for nothing.
 * `quiet` keeps the dry run out of the logs — the thunk's real run logs.
 *
 * NOT the same question as "is anything missing from `validCartItems`":
 * `isInvalidModGroup` is also true for an OVER-selected group, which
 * `getValidCartItems` drops but the ladder refuses to touch ("not ours to
 * fix", `requiredShortfall`). A cart whose only defect is over-selection reads
 * false here, so it stays on today's path and never lands in the metric.
 */
export const selectNeedsAutofill = (state: any): boolean => {
  const {
    config: {
      FEATURE_FLAG_SUPPRESS_UPSELL,
      FEATURE_FLAG_AUTO_COMBO,
      FEATURE_FLAG_BM_AUTO_COMBO,
    },
    menu: { brandMenu, modSymbolMapping, menuRes },
    cart: { cartItems },
    autoCombo: { autoCombinedCartItems },
  } = state;

  return (
    computeAutofillPlan({
      cartItems,
      autoCombinedCartItems,
      brandMenu,
      modSymbolMapping,
      menuRes,
      suppressUpsellEnabled: FEATURE_FLAG_SUPPRESS_UPSELL,
      autoComboEnabled: FEATURE_FLAG_AUTO_COMBO,
      bmAutoComboEnabled: FEATURE_FLAG_BM_AUTO_COMBO,
      quiet: true,
    }).selections.length > 0
  );
};

/**
 * "May this auto submit pass sendOrder's isAIAutoMode guard?" — the HITL-1607
 * gate the auto-submit paths pass as `bypassAIAutoMode`.
 *
 * WHY THE GUARD MUST BE BYPASSED HERE. `sendOrder` refuses to send while
 * `isAIAutoMode` is true, and that flag stays true until the agent's FIRST
 * interception. On an escalated car the agent never touches (assigned, then
 * the car exits — see HITL-1607's example session), the AI has stood down and
 * the guard blocks the only sender left, so the order is silently lost.
 *
 * WHY AN UNCONDITIONAL BYPASS IS SAFE (no escalation-state gating). Three
 * facts: the AI does not order past a car exit — even an order-continuation
 * red (`ai_requested_hitl_takeover: false`) ends in a flash-red halt, so by
 * the time an auto-submit trigger fires there is no AI submission left to
 * race; a car the AI DID finalize has its HITL cart cleared on that final, so
 * this path lands on an empty cart and cancels rather than re-sending; and if
 * a second final somehow goes out anyway, POSLINK rejects the duplicate — no
 * second check. Gating this on the takeover flag was tried first and only
 * re-created the gap for every escalation shape the flag doesn't cover.
 *
 * THE QUANTITY-HOLD EXCLUSION — the one hold-back, and it is NOT about
 * duplicates (POSLINK cannot help: no prior final exists). A session
 * escalated because the AI clamped a suspicious quantity carries a cart that
 * is known-wrong by construction — the AI itself refuses to send it even
 * unattended, because losing it beats charging the customer for a number they
 * never said.
 *
 * The hold is read from the structured `order_held` flag (PRVA-15007), which
 * the AI computes fresh on every emission — so `false` is as meaningful as
 * `true` (the held line was corrected, the cart is provably fine) and, when
 * ANY event has carried the field, it is authoritative: no text sniffing.
 * Two carriers, because a late-joining page may only ever see the second:
 * the ai_escalation event (kept in `ai.orderHeld`) and the RED ai_status
 * (`ai.aiStatus.order_held` — also the copy the task hands over at pickup).
 * The pickup copy has its own deploy lag: a re-hydrated ai_status carries a
 * field only once the TASK-ROUTER forwards it (restaurantSlice's HITL-1356
 * note), so until that ships, the late-join path sits on the same text
 * fallback below that the flag supersedes.
 *
 * WHY NON-STICKY `orderHeld` SURVIVES REPLAY. `requiresTakeover` right above
 * it in the slice is sticky precisely to defend against replayed events, so
 * latest-value here looks wrong at first read. It is safe today only because
 * two unrelated filters keep stale escalations out: filterBufferedEvents
 * drops ai_escalation from the pickup buffer, and the messaging middleware's
 * refetch only runs while aiStatus is still green. A change to either filter
 * re-opens replay downgrades of a live hold — this line is the tripwire.
 *
 * The reason-text match below survives ONLY as the deploy-skew fallback for
 * gpt-context versions that predate the flag (field never seen ⇒ null).
 * Deliberately broad — /quantit/i, not the exact phrasing: a false match
 * falls back to today's behaviour (order dropped — benign), while a miss
 * auto-submits an overcharge, so it must fail toward NOT sending. Delete it
 * once every environment runs a flag-emitting gpt-context.
 *
 * Plain function over state, not a memoized selector, for the same reason as
 * `selectNeedsAutofill`: it is read once, at the instant a trigger fires.
 */
export const selectAutoSubmitBypassesAIAutoMode = (state: any): boolean => {
  const {
    config: { FEATURE_FLAG_AUTO_SUBMIT_AI_AUTO_MODE_BYPASS },
    ai: { orderHeld, aiEscalationReason, aiStatus },
  } = state;

  if (!FEATURE_FLAG_AUTO_SUBMIT_AI_AUTO_MODE_BYPASS) return false;

  const structuredHeld = orderHeld ?? aiStatus?.order_held ?? null;
  if (structuredHeld !== null) return !structuredHeld;

  const escalationText = `${aiEscalationReason || ''} ${
    aiStatus?.message || ''
  }`;
  if (/quantit/i.test(escalationText)) return false;

  return true;
};

export const autofillAndSubmitFinalOrder = createAsyncThunk<
  IAutofillFinalOrderResult,
  IAutofillFinalOrderArgs,
  { state: any }
>(
  'order/autofillAndSubmitFinalOrder',
  async (
    {
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
      source = AutofillFinalOrderSource.staffIntervention,
      bypassAIAutoMode = true,
    },
    { dispatch, getState }
  ) => {
    const {
      config: {
        FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION,
        FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT,
        FEATURE_FLAG_SUPPRESS_UPSELL,
        FEATURE_FLAG_AUTO_COMBO,
        FEATURE_FLAG_BM_AUTO_COMBO,
      },
      menu: { brandMenu, modSymbolMapping, menuRes, unavailableItemsByName },
      cart: { cartItems },
      autoCombo: { autoCombinedCartItems },
      restaurant,
      sessionBoundary: { sessionId },
    } = getState();

    const restaurantCode =
      restaurant?.selectedRestaurantDetails?.restaurantCode || '';

    // Each calling path is governed by ITS OWN kill switch (HITL-1562): the
    // intervention path by the original flag, the auto-submit paths by the new
    // one — so either rolls out or dies without touching the other. The call
    // sites check the same flag before dispatching; reaching this branch means
    // a race or a bug, and the `disabled` outcome keeps it visible.
    const governingFlagOn =
      source === AutofillFinalOrderSource.staffIntervention
        ? FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION
        : FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT;
    if (!governingFlagOn) {
      return {
        outcome: AutofillFinalOrderOutcome.disabled,
        fills: [],
        unfillable: [],
      };
    }

    // The whole legacy-auto-combo story lives on computeAutofillPlan.
    const { legacyAutoCombo, selections, fills, unfillable } =
      computeAutofillPlan({
        cartItems,
        autoCombinedCartItems,
        brandMenu,
        modSymbolMapping,
        menuRes,
        suppressUpsellEnabled: FEATURE_FLAG_SUPPRESS_UPSELL,
        autoComboEnabled: FEATURE_FLAG_AUTO_COMBO,
        bmAutoComboEnabled: FEATURE_FLAG_BM_AUTO_COMBO,
      });
    if (legacyAutoCombo) {
      logger.warn({
        message: `${AUTOFILL_FINAL_ORDER_LOG_TOKEN}: legacy auto-combined cart is populated; submitting without filling (see HITL-1556)`,
        sessionId,
        restaurantCode,
      });
    }

    // MUST be set before the fill lands. Filling flips items from invalid to
    // valid, so `selectValidCartItems` changes and OrderReview's cart-change
    // effect (OrderReview.tsx:105) fires — sending an interim order right behind
    // our final one, and bumping `menuModuleRequestSeqId` so the final's own
    // callbacks are gated out as stale. `sendOrder` drops non-final sends while
    // this is set.
    dispatch(orderActions.setIsAutofillFinalInFlight(true));

    try {
      if (selections.length) {
        dispatch(
          cartActions.selectModifiers({ selections, unavailableItemsByName })
        );
      }

      // The cart is now as complete as it can be made. Everything from here is
      // the ordinary final-order path.
      // Cast: this thunk is typed `{ state: any }` (the store's RootState is not
      // importable here without a cycle), which loosens thunkAPI's dispatch past
      // what a nested thunk's signature wants.
      const sent = await (dispatch as any)(
        sendOrder({
          isFinal: true,
          // Staff intervention: every escalated car, AI up or down (true, the
          // default). HITL-1562 paths: false — the AI may still hold the car,
          // and the guard is what prevents a double submission.
          bypassAIAutoMode,
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );

      // OUTCOME IS DECIDED AFTER THE SEND, and from what the send actually did.
      // Reporting it beforehand meant a cart that shipped nothing — every item
      // still invalid, an empty cart, a rejected request — was recorded as
      // `submitted_*`. On a path whose only surface is this metric, that is a
      // success signal for a failure. It is safe to be late because the caller
      // holds the session open until this thunk settles (see the header); the
      // New Relic log would survive teardown regardless, being HTTP rather than
      // the websocket, but the event-stream copy would not.
      // "Did the final order pass the Menu Module layer" — that, and only that,
      // is what this feature succeeding means. `sendOrder` returns true when the
      // Menu Module accepted it.
      //
      // The previous signal counted `requestsOrder` growth, which is wrong on
      // the DEFAULT path: a successful final runs `finishOrderResetValues`,
      // which collapses that array to the single final request
      // (orderSlice.ts, HITL-1527). So the count did not grow and every
      // successful submit recorded `not_submitted` — the metric read backwards
      // for the common case.
      const shipped = sent?.payload === true;
      const outcome = !shipped
        ? AutofillFinalOrderOutcome.notSubmitted
        : unfillable.length
        ? AutofillFinalOrderOutcome.submittedIncomplete
        : fills.length
        ? AutofillFinalOrderOutcome.filledAndSubmitted
        : AutofillFinalOrderOutcome.submittedUnchanged;

      const result: IAutofillFinalOrderResult = { outcome, fills, unfillable };
      // FLAT, deliberately. `logger`'s `stringifyWithDepth` defaults to depth 2
      // and replaces any object it meets AT that depth with the literal
      // '{...}'. Nested under `moreInfo` these arrays sat at exactly depth 2, so
      // every production log read `fills: '{...}'` — the per-group, per-value
      // detail the ticket requires was never reaching New Relic, and
      // `not_submitted` could not be told apart from a cart the fill could not
      // rescue. At the top level the array sits at depth 1 and its elements are
      // plain strings, which pass through as primitives. Keep these flat, and
      // keep them strings.
      const logPayload = {
        message: `${AUTOFILL_FINAL_ORDER_LOG_TOKEN}: ${outcome}`,
        sessionId,
        restaurantCode,
        source,
        bypassAIAutoMode,
        fillCount: fills.length,
        fills,
        unfillableCount: unfillable.length,
        unfillable,
      };
      if (!shipped || unfillable.length) {
        logger.warn(logPayload);
      } else {
        logger.info(logPayload);
      }

      dispatch(
        messagingActions.sendInfo({
          data: {
            type: AUTOFILL_FINAL_ORDER_METRIC,
            schema_version: AUTOFILL_FINAL_ORDER_METRIC_SCHEMA_VERSION,
            outcome,
            source,
            bypass_ai_auto_mode: bypassAIAutoMode,
            fills,
            fill_count: fills.length,
            unfillable,
          },
        } as any)
      );

      return result;
    } finally {
      // The suppression window closes as soon as the final send has been made.
      // In `finally` so a throw cannot strand it on — leaving it set would mute
      // every interim order for the rest of the session.
      dispatch(orderActions.setIsAutofillFinalInFlight(false));
    }
  }
);
